import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { Badge, Button, Card, CardContent, Textarea } from '@mlm/ui';
import { hideQuestion, listInbox, replyToQuestion } from '../lib/api';

/** Q&A inbox — every learner question across the creator's courses, newest first. */
export function QaPage() {
  const qc = useQueryClient();
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['creator-inbox'],
    queryFn: listInbox,
  });
  // `?question=` arrives from a `creator_question` bell. Driven by an effect,
  // not a useState initialiser: the bell lives in the persistent shell, so a
  // second click while already on this page only changes the search string.
  const [params] = useSearchParams();
  const wanted = params.get('question');
  const [openId, setOpenId] = useState<string | null>(wanted);
  const [draft, setDraft] = useState('');
  const [filter, setFilter] = useState<'unanswered' | 'all'>(wanted ? 'all' : 'unanswered');
  useEffect(() => {
    if (!wanted) return;
    setOpenId(wanted);
    setFilter('all');
  }, [wanted]);

  const reply = useMutation({
    meta: { successMessage: 'Reply posted.' },
    mutationFn: () => replyToQuestion(openId!, draft.trim()),
    onSuccess: () => {
      setOpenId(null);
      setDraft('');
      qc.invalidateQueries({ queryKey: ['creator-inbox'] });
    },
  });
  const hide = useMutation({
    mutationFn: ({ id, hidden }: { id: string; hidden: boolean }) => hideQuestion(id, hidden),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['creator-inbox'] }),
  });

  const shown = rows.filter((r) => filter === 'all' || !r.creatorReplied);

  return (
    <div className="max-w-4xl space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Questions</h1>
        <div className="flex gap-1 rounded border p-0.5 text-xs">
          <button
            className={`rounded px-2 py-1 ${filter === 'unanswered' ? 'bg-accent font-medium' : ''}`}
            onClick={() => setFilter('unanswered')}
          >
            Unanswered
          </button>
          <button
            className={`rounded px-2 py-1 ${filter === 'all' ? 'bg-accent font-medium' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
        </div>
      </div>
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
          ) : shown.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {filter === 'unanswered'
                ? 'All caught up — no unanswered questions.'
                : 'No questions yet.'}
            </p>
          ) : (
            <ul className="divide-y">
              {shown.map((r) => (
                <li key={r.id} className={`p-4 ${r.hidden ? 'opacity-60' : ''}`}>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{r.authorName ?? 'Learner'}</span>
                    <span>· {new Date(r.createdAt).toLocaleString()}</span>
                    <span>·</span>
                    <Link to={`/courses/${r.courseId}`} className="hover:underline">
                      {r.lessonTitle}
                    </Link>
                    {r.creatorReplied && <Badge variant="outline">Answered</Badge>}
                    {r.hidden && <Badge variant="secondary">Hidden</Badge>}
                    {r.replyCount > 0 && (
                      <span>
                        · {r.replyCount} repl{r.replyCount === 1 ? 'y' : 'ies'}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 whitespace-pre-line text-sm">{r.body}</p>
                  {openId === r.id ? (
                    <div className="mt-3 space-y-2">
                      <Textarea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        rows={3}
                        maxLength={2000}
                        placeholder="Write your answer…"
                      />
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="ghost" onClick={() => setOpenId(null)}>
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => reply.mutate()}
                          disabled={!draft.trim() || reply.isPending}
                        >
                          Post answer
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2 flex gap-3 text-xs">
                      <button
                        className="font-medium text-primary hover:underline"
                        onClick={() => {
                          setOpenId(r.id);
                          setDraft('');
                        }}
                      >
                        Answer
                      </button>
                      <button
                        className="text-muted-foreground hover:underline"
                        onClick={() => hide.mutate({ id: r.id, hidden: !r.hidden })}
                      >
                        {r.hidden ? 'Unhide' : 'Hide'}
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
